/**
 * @file version-mixin.ts
 *
 * Owns component version metadata and version-aggregation methods. The
 * component library version is read from package.json at build time; TrCAD
 * versions are fetched lazily from as-configurator on first request and
 * cached for subsequent calls.
 *
 * Exposes: version, getVersion, getTrCADVersions, getVersions
 */
import packageJson from '../../../../package.json';
import { AsConfigurator } from '../../paramate/as-configurator.ts';
import { devlog } from '../../../logic/utils.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by VersionMixin. */
export interface VersionMixinMembers {
  readonly version: string;
  getVersion(): string;
  getTrCADVersions(): Promise<{ trCad: string | undefined; script: string | undefined }>;
  getVersions(): Promise<{ components: string; app: string | null; trCad: string | undefined; trCadScript: string | undefined }>;
}

export const VersionMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class VersionMixin extends Base {
    readonly version: string = packageJson.version;
    private trcadVersions: { trCad: string; script: string } | undefined;

    getVersion() { return this.version; }

    async getTrCADVersions() {
      const output: { trCad: string | undefined; script: string | undefined } = { trCad: undefined, script: undefined };
      if (!this.trcadVersions) {
        const configuratorEl = document.querySelector('as-configurator') as AsConfigurator;
        if (!configuratorEl) { console.warn('[VERSIONING] as-configurator element not found'); return output; }
        try {
          const params = await configuratorEl.getParams();
          const trCadScriptVer = params?.['main:metadata:scriptVersion']?.default;
          const trCadVer = params?.['main:metadata:trcadVersion']?.default;
          if (!trCadVer) devlog('[VERSIONING] trcadVersion not found in params main:metadata');
          if (!trCadScriptVer) devlog('[VERSIONING] scriptVersion not found in params main:metadata');
          output.trCad = trCadVer;
          output.script = trCadScriptVer;
          devlog('[VERSIONING]', output);
          this.trcadVersions = output as { trCad: string; script: string };
          return output;
        } catch (err) {
          console.error('[VERSIONING] Error getting TrCAD version:', err);
          return output;
        }
      } else {
        return this.trcadVersions;
      }
    }

    async getVersions() {
      const { trCad, script } = (await this.getTrCADVersions()) ?? {};
      return { components: this.version, app: this.getAttribute('app-version'), trCad, trCadScript: script };
    }
  };
